/**
 * Internal DNC store — master enforcement toggle, suppression rules,
 * and the recent-blocked list.
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type { PREDecisionLog } from '@respectly/shared-types';

export interface InternalDncRules {
  areaCodes: string[];
  exactNumbers: string[];
  prefixes: string[];
}

export interface InternalDncConfig {
  enabled: boolean;
  rules: InternalDncRules;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
}

export interface SuppressedRecipient {
  id: string;
  status: string;
  smsExternalId: string | null;
  sentAt: string | null;
  internalDncReason: string | null;
  contactId: string;
  campaignId: string;
  contact: {
    id: string;
    firstName: string;
    lastName: string | null;
    phone: string | null;
    tenantId: string;
  } | null;
  campaign: {
    id: string;
    name: string;
    tenantId: string;
  } | null;
}

export interface RecentSuppressedSend {
  recipient: SuppressedRecipient;
  decisionLog: PREDecisionLog | null;
}

export interface ContactSearchResult {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  tenant: { id: string; name: string } | null;
}

interface State {
  config: InternalDncConfig | null;
  isLoadingConfig: boolean;
  isSavingConfig: boolean;
  configError: string | null;

  recentSends: RecentSuppressedSend[];
  isLoadingRecentSends: boolean;
  recentSendsError: string | null;

  loadConfig: () => Promise<void>;
  saveConfig: (next: { enabled: boolean; rules: InternalDncRules }) => Promise<boolean>;
  loadRecentSends: () => Promise<void>;
  searchContacts: (query: string) => Promise<ContactSearchResult[]>;
}

const EMPTY_RULES: InternalDncRules = { areaCodes: [], exactNumbers: [], prefixes: [] };

export const useInternalDncStore = create<State>((set) => ({
  config: null,
  isLoadingConfig: false,
  isSavingConfig: false,
  configError: null,

  recentSends: [],
  isLoadingRecentSends: false,
  recentSendsError: null,

  loadConfig: async () => {
    set({ isLoadingConfig: true, configError: null });
    try {
      const api = getApiClient();
      const response = await api.get<InternalDncConfig>(API_ENDPOINTS.SUPER_ADMIN.INTERNAL_DNC.GET);
      set({
        config: { ...response, rules: response.rules ?? EMPTY_RULES },
        isLoadingConfig: false,
      });
    } catch (err) {
      console.error('[InternalDnc] loadConfig failed:', err);
      set({
        config: { enabled: false, rules: EMPTY_RULES, updatedAt: null, updatedBy: null, updatedByName: null },
        isLoadingConfig: false,
        configError: 'Failed to load Internal DNC config',
      });
    }
  },

  saveConfig: async (next) => {
    set({ isSavingConfig: true, configError: null });
    try {
      const api = getApiClient();
      const response = await api.put<InternalDncConfig>(API_ENDPOINTS.SUPER_ADMIN.INTERNAL_DNC.UPDATE, next);
      set({
        config: { ...response, rules: response.rules ?? EMPTY_RULES },
        isSavingConfig: false,
      });
      return true;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save Internal DNC config';
      console.error('[InternalDnc] saveConfig failed:', err);
      set({ isSavingConfig: false, configError: message });
      return false;
    }
  },

  loadRecentSends: async () => {
    set({ isLoadingRecentSends: true, recentSendsError: null });
    try {
      const api = getApiClient();
      const response = await api.get<RecentSuppressedSend[]>(API_ENDPOINTS.SUPER_ADMIN.INTERNAL_DNC.RECENT_SENDS);
      set({ recentSends: response, isLoadingRecentSends: false });
    } catch (err) {
      console.error('[InternalDnc] loadRecentSends failed:', err);
      set({
        recentSends: [],
        isLoadingRecentSends: false,
        recentSendsError: 'Failed to load recently blocked sends',
      });
    }
  },

  searchContacts: async (query) => {
    const q = query.trim();
    if (q.length < 2) return [];
    try {
      const api = getApiClient();
      const url = `${API_ENDPOINTS.SUPER_ADMIN.CONTACTS.SEARCH}?q=${encodeURIComponent(q)}&limit=25`;
      return await api.get<ContactSearchResult[]>(url);
    } catch (err) {
      console.error('[InternalDnc] searchContacts failed:', err);
      return [];
    }
  },
}));
